/**
 * Client service for sector operations requests and request kinds.
 * R39 A7.a (plan docs/plans/sector-operations.md §2.2 SO-P64; §4 ops_requests, ops_request_kinds; §6 A7.a).
 * Wraps callables for writes and listens to firestore ops_requests / ops_request_kinds for reads.
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  type Unsubscribe,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { OpsSubscriptionAccessOptions } from './opsRostersService'
import type {
  OpsRequest,
  OpsRequestKind,
  OpsRequestState,
  UpsertRequestKindInput,
  ListRequestKindsInput,
  RaiseRequestInput,
  AttachRequestPhotosInput,
  AcknowledgeRequestInput,
  StartRequestInput,
  AssignRequestInput,
  MarkRequestDoneInput,
  MarkRequestDoneResult,
  VerifyRequestInput,
  ReopenRequestInput,
  CancelRequestInput,
  OpsRequestEvent,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables (Writes)
// ---------------------------------------------------------------------------

export async function upsertRequestKind(
  input: UpsertRequestKindInput,
): Promise<{ success: boolean; kind: OpsRequestKind; replayed?: boolean }> {
  return callable<
    UpsertRequestKindInput,
    { success: boolean; kind: OpsRequestKind; replayed?: boolean }
  >('upsertRequestKind', input)
}

export async function listRequestKinds(
  input: ListRequestKindsInput,
): Promise<{ kinds: OpsRequestKind[] }> {
  return callable<ListRequestKindsInput, { kinds: OpsRequestKind[] }>(
    'listRequestKinds',
    input,
  )
}

export async function raiseRequest(
  input: RaiseRequestInput,
): Promise<{ requestId: string; state: OpsRequestState; replayed?: boolean }> {
  return callable<
    RaiseRequestInput,
    { requestId: string; state: OpsRequestState; replayed?: boolean }
  >('raiseRequest', input)
}

export async function attachRequestPhotos(
  input: AttachRequestPhotosInput,
): Promise<{ success: boolean; photoCount: number; replayed?: boolean }> {
  return callable<
    AttachRequestPhotosInput,
    { success: boolean; photoCount: number; replayed?: boolean }
  >('attachRequestPhotos', input)
}

export async function acknowledgeRequest(
  input: AcknowledgeRequestInput,
): Promise<{ requestId: string; state: OpsRequestState; replayed?: boolean }> {
  return callable<
    AcknowledgeRequestInput,
    { requestId: string; state: OpsRequestState; replayed?: boolean }
  >('acknowledgeRequest', input)
}

export async function startRequest(
  input: StartRequestInput,
): Promise<{ requestId: string; state: OpsRequestState; replayed?: boolean }> {
  return callable<
    StartRequestInput,
    { requestId: string; state: OpsRequestState; replayed?: boolean }
  >('startRequest', input)
}

export async function assignRequest(
  input: AssignRequestInput,
): Promise<{ requestId: string; assigneeUid: string; replayed?: boolean }> {
  return callable<
    AssignRequestInput,
    { requestId: string; assigneeUid: string; replayed?: boolean }
  >('assignRequest', input)
}

export async function markRequestDone(
  input: MarkRequestDoneInput,
): Promise<MarkRequestDoneResult> {
  return callable<MarkRequestDoneInput, MarkRequestDoneResult>('markRequestDone', input)
}

export async function verifyRequest(
  input: VerifyRequestInput,
): Promise<{ requestId: string; state: OpsRequestState; selfVerified?: boolean; replayed?: boolean }> {
  return callable<
    VerifyRequestInput,
    { requestId: string; state: OpsRequestState; selfVerified?: boolean; replayed?: boolean }
  >('verifyRequest', input)
}

export async function reopenRequest(
  input: ReopenRequestInput,
): Promise<{ requestId: string; state: OpsRequestState; replayed?: boolean }> {
  return callable<
    ReopenRequestInput,
    { requestId: string; state: OpsRequestState; replayed?: boolean }
  >('reopenRequest', input)
}

export async function cancelRequest(
  input: CancelRequestInput,
): Promise<{ requestId: string; state: OpsRequestState; replayed?: boolean }> {
  return callable<
    CancelRequestInput,
    { requestId: string; state: OpsRequestState; replayed?: boolean }
  >('cancelRequest', input)
}

// ---------------------------------------------------------------------------
// Firestore Subscriptions (Reads)
// ---------------------------------------------------------------------------

export interface SubscribeOpsRequestsFilters extends OpsSubscriptionAccessOptions {
  callerUid?: string
  placeId?: string
  assetId?: string
  state?: OpsRequestState
  assigneeUid?: string
  viewerUid?: string
  viewerRoleKeys?: string[]
}

export interface OpsRequestQueryOptions {
  callerUid?: string
  viewerUid?: string
  assetId?: string
  isElevated?: boolean
  isRestricted?: boolean
  viewerRoleKeys?: string[]
}

/**
 * Pure builder for Firestore query constraints used by subscribeOpsRequests and rules emulator tests.
 * R39 A7.b.fix1 F10 & F8. Each stream gets limit(200).
 */
export function buildOpsRequestQueryConstraints(
  workspaceId: string,
  options: OpsRequestQueryOptions,
): QueryConstraint[][] {
  const callerUid = options.callerUid?.trim() || options.viewerUid?.trim()
  if (!callerUid) {
    return []
  }

  const queries: QueryConstraint[][] = []
  const assetFilter: QueryConstraint[] = options.assetId
    ? [where('assetId', '==', options.assetId.trim())]
    : []

  // 1. Unrestricted requests:
  // Restricted-scope member: split by readAccess == null and readAccess array-contains callerUid
  if (options.isRestricted === true) {
    queries.push([
      where('workspaceId', '==', workspaceId),
      ...assetFilter,
      where('restricted', '==', false),
      where('readAccess', '==', null),
      orderBy('createdAt', 'desc'),
      limit(200),
    ])
    queries.push([
      where('workspaceId', '==', workspaceId),
      ...assetFilter,
      where('restricted', '==', false),
      where('readAccess', 'array-contains', callerUid),
      orderBy('createdAt', 'desc'),
      limit(200),
    ])
  } else {
    // Plain / elevated member
    queries.push([
      where('workspaceId', '==', workspaceId),
      ...assetFilter,
      where('restricted', '==', false),
      orderBy('createdAt', 'desc'),
      limit(200),
    ])
  }

  // 2. Raised by me (always allowed by opsReqR)
  queries.push([
    where('workspaceId', '==', workspaceId),
    ...assetFilter,
    where('raisedByUid', '==', callerUid),
    orderBy('createdAt', 'desc'),
    limit(200),
  ])

  // 3. Assigned to me
  queries.push([
    where('workspaceId', '==', workspaceId),
    ...assetFilter,
    where('assigneeUid', '==', callerUid),
    orderBy('createdAt', 'desc'),
    limit(200),
  ])

  // 4. Restricted requests for Owner/Admin
  if (options.isElevated === true) {
    queries.push([
      where('workspaceId', '==', workspaceId),
      ...assetFilter,
      where('restricted', '==', true),
      orderBy('createdAt', 'desc'),
      limit(200),
    ])
  }

  // 5. Restricted requests for viewer roles (e.g. INCIDENT_HANDLER)
  if (options.viewerRoleKeys && options.viewerRoleKeys.length > 0) {
    queries.push([
      where('workspaceId', '==', workspaceId),
      ...assetFilter,
      where('restricted', '==', true),
      where(
        'viewerRoleKeys',
        'array-contains-any',
        options.viewerRoleKeys.slice(0, 10),
      ),
      orderBy('createdAt', 'desc'),
      limit(200),
    ])
  }

  return queries
}

export function subscribeOpsRequests(
  workspaceId: string,
  onUpdateOrOptions: ((requests: OpsRequest[]) => void) | SubscribeOpsRequestsFilters,
  onErrorOrCallback?: ((err: Error) => void) | ((requests: OpsRequest[]) => void),
  maybeOptionsOrOnError?: SubscribeOpsRequestsFilters | ((err: Error) => void),
): Unsubscribe {
  let onUpdate: (requests: OpsRequest[]) => void
  let onError: ((err: Error) => void) | undefined
  let options: SubscribeOpsRequestsFilters | undefined

  if (typeof onUpdateOrOptions === 'function') {
    onUpdate = onUpdateOrOptions
    if (typeof onErrorOrCallback === 'function') {
      onError = onErrorOrCallback as (err: Error) => void
    }
    if (typeof maybeOptionsOrOnError === 'object' && maybeOptionsOrOnError !== null) {
      options = maybeOptionsOrOnError
    }
  } else {
    options = onUpdateOrOptions
    onUpdate = onErrorOrCallback as (requests: OpsRequest[]) => void
    if (typeof maybeOptionsOrOnError === 'function') {
      onError = maybeOptionsOrOnError
    }
  }

  const callerUid = options?.callerUid?.trim() || options?.viewerUid?.trim()
  if (!callerUid) {
    onError?.(new Error('callerUid is required for subscribeOpsRequests'))
    return () => undefined
  }

  const coll = collection(firestoreDb, 'ops_requests')
  const unsubs: Unsubscribe[] = []
  const streamDocs: OpsRequest[][] = []

  const mergeAndEmit = () => {
    const map = new Map<string, OpsRequest>()
    for (const list of streamDocs) {
      for (const d of list) {
        map.set(d.id, d)
      }
    }
    let merged = Array.from(map.values())
    if (options?.placeId) {
      merged = merged.filter((d) => d.placeId === options.placeId)
    }
    if (options?.assetId) {
      merged = merged.filter((d) => d.assetId === options.assetId)
    }
    if (options?.state) {
      merged = merged.filter((d) => d.state === options.state)
    }
    if (options?.assigneeUid) {
      merged = merged.filter((d) => d.assigneeUid === options.assigneeUid)
    }
    merged.sort(
      (a, b) => (b.createdAt || 0) - (a.createdAt || 0) || b.id.localeCompare(a.id),
    )
    onUpdate(merged)
  }

  const addQueryStream = (constraints: QueryConstraint[]) => {
    const streamIndex = streamDocs.length
    streamDocs.push([])
    const q = query(coll, ...constraints)
    const unsub = onSnapshot(
      q,
      (snap) => {
        streamDocs[streamIndex] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<OpsRequest, 'id'>),
        }))
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )
    unsubs.push(unsub)
  }

  const queryConstraintGroups = buildOpsRequestQueryConstraints(workspaceId, {
    callerUid,
    isElevated: options?.isElevated,
    isRestricted: options?.isRestricted,
    viewerRoleKeys: options?.viewerRoleKeys,
  })

  for (const constraints of queryConstraintGroups) {
    addQueryStream(constraints)
  }

  return () => {
    for (const unsub of unsubs) {
      unsub()
    }
  }
}

export function subscribeOpsRequestEvents(
  requestId: string,
  onUpdate: (events: OpsRequestEvent[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const coll = collection(firestoreDb, 'ops_requests', requestId, 'events')
  const q = query(coll, orderBy('at', 'asc'))
  return onSnapshot(
    q,
    (snap) => {
      const items: OpsRequestEvent[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsRequestEvent, 'id'>),
      }))
      onUpdate(items)
    },
    onError,
  )
}
